// Calls to the web API (/app/v1) with the Keycloak access token; errors become ProblemError.
import { isProblem, ProblemError } from "@cosmosai/api-client";
import { useMemo } from "react";
import { useRuntimeConfig } from "../app/config-context";
import { useAuth } from "../features/auth/auth-context";
import type { AuthPort } from "../features/auth/auth-port";
import { reauthenticate } from "./unauthorized";

/** An answer's body with its headers, for ETag and Last-Modified. */
export interface AppAnswer<T> {
  body: T;
  headers: Headers;
}

export type AppFetch = (<T>(path: string, init?: RequestInit) => Promise<T>) & {
  withHeaders<T>(path: string, init?: RequestInit): Promise<AppAnswer<T>>;
};

/** A fetch for /app/v1 that sends the bearer token and signs in again on a 401. */
export function createAppFetch(
  auth: Pick<AuthPort, "accessToken" | "signIn">,
  baseUrl = "",
): AppFetch {
  const base = `${baseUrl.replace(/\/$/, "")}/app/v1`;
  const withHeaders = async <T>(path: string, init?: RequestInit): Promise<AppAnswer<T>> => {
    const token = await auth.accessToken();
    const response = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        accept: "application/json",
        ...(init?.body ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (response.status === 401 && token) await reauthenticate(auth);
    if (response.status === 204) return { body: undefined as T, headers: response.headers };
    const body: unknown = await response.json().catch(() => undefined);
    if (!response.ok) throw problemFrom(response, body);
    return { body: body as T, headers: response.headers };
  };
  const appFetch = async <T>(path: string, init?: RequestInit): Promise<T> =>
    (await withHeaders<T>(path, init)).body;
  return Object.assign(appFetch, { withHeaders });
}

/** The error of a failed answer: its problem document, or one made of its status. */
export function problemFrom(response: Response, body: unknown): ProblemError {
  const detail = messageOf(body);
  const problem = isProblem(body)
    ? body
    : { status: response.status, title: response.statusText, ...(detail ? { detail } : {}) };
  return new ProblemError(problem, response.headers.get("x-request-id") ?? undefined);
}

/** The web API fetch of this page, built once per AuthPort. */
export function useAppFetch(): AppFetch {
  const auth = useAuth();
  const { webApiUrl } = useRuntimeConfig();
  return useMemo(() => createAppFetch(auth, webApiUrl), [auth, webApiUrl]);
}

const messageOf = (body: unknown): string | undefined =>
  typeof body === "object" && body !== null && "message" in body ? String(body.message) : undefined;
